import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Copy, Check } from 'lucide-react';
import { fetchTivConversation, updateTivConversation, validateTivConversation } from '../api/tiv';

function AutoResizeTextarea({ value, onChange, className }) {
  const ref = useRef(null);
  useEffect(() => {
    if (ref.current) {
      ref.current.style.height = 'auto';
      ref.current.style.height = `${ref.current.scrollHeight}px`;
    }
  }, [value]);
  return (
    <textarea
      ref={ref}
      value={value}
      onChange={onChange}
      rows={1}
      className={className}
    />
  );
}

function MaxCharEditor({ value, onChange, onBlur }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState('');
  const inputRef              = useRef(null);

  function startEdit() {
    setDraft(String(value ?? ''));
    setEditing(true);
    setTimeout(() => inputRef.current?.select(), 0);
  }

  function commit() {
    const n = parseInt(draft, 10);
    const valid = !isNaN(n) && n > 0;
    if (valid) onChange(n);
    setEditing(false);
    onBlur(valid ? n : null);
  }

  function handleKey(e) {
    if (e.key === 'Enter') commit();
    if (e.key === 'Escape') setEditing(false);
  }

  if (editing) {
    return (
      <input
        ref={inputRef}
        value={draft}
        onChange={e => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={handleKey}
        className="w-10 text-xs text-zinc-300 bg-white/[0.08] border border-white/[0.15] rounded px-1.5 py-0.5 outline-none text-right tabular-nums"
      />
    );
  }

  return (
    <button
      onClick={startEdit}
      title="Modifier la limite"
      className="text-xs tabular-nums underline decoration-dotted underline-offset-2 text-zinc-500 hover:text-zinc-300 transition-colors"
    >
      {value}
    </button>
  );
}

export default function TivLetterPage() {
  const { id }   = useParams();
  const navigate = useNavigate();

  const [loading,    setLoading]    = useState(true);
  const [title,      setTitle]      = useState('');
  const [status,     setStatus]     = useState('');
  const [maxChar,    setMaxChar]    = useState(null);
  const [editMode,   setEditMode]   = useState(false);
  const [text,       setText]       = useState('');
  const [hasLetter,  setHasLetter]  = useState(false);
  const [saving,     setSaving]     = useState(false);
  const [validating, setValidating] = useState(false);
  const [copied,     setCopied]     = useState(false);

  useEffect(() => {
    async function load() {
      try {
        const conv = await fetchTivConversation(id);
        setTitle(conv.title || 'Ma lettre');
        setMaxChar(conv.lm_max_char || null);
        setStatus(conv.status || null);
        if (conv.lm_merged) {
          setText(conv.lm_merged);
          setHasLetter(true);
        } else {
          setText([conv.lm_declic, conv.lm_formation, conv.lm_profil, conv.lm_profile_2, conv.lm_projection]
            .filter(Boolean).join('\n\n'));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, [id]);

  const charCount = text.length;
  const overLimit = maxChar !== null && charCount > maxChar;

  async function patch(newMaxChar) {
    await updateTivConversation(Number(id), {
      lm_merged:   text,
      lm_max_char: newMaxChar !== undefined ? newMaxChar : maxChar,
    });
  }

  async function handleFinishEdit() {
    setSaving(true);
    try {
      await patch();
      setEditMode(false);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }

  async function handleValidate() {
    setValidating(true);
    try {
      await validateTivConversation(Number(id));
      navigate('/tiv');
    } catch (e) {
      console.error(e);
      setValidating(false);
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  }

  if (loading) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-900">
        <div className="w-5 h-5 rounded-full border-2 border-blue-500 border-t-transparent animate-spin" />
      </div>
    );
  }

  return (
    <div className="flex flex-1 flex-col h-dvh bg-gray-900">
      {/* Header */}
      <div className="border-b border-white/[0.06] shrink-0">
        <div className="flex items-center gap-3 px-4 py-3 max-w-2xl mx-auto w-full">
          <button
            onClick={() => status === 'draft' ? navigate(`/tiv/lettre-motivation/${id}`) : navigate(`/tiv`)}
            className="p-1 rounded-lg hover:bg-white/[0.05] transition-colors text-zinc-400"
          >
            <ArrowLeft size={20} />
          </button>
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-semibold text-white truncate">{title}</h1>
            <p className="text-xs text-zinc-500">Lettre de motivation · Tiv</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="px-4 py-5 max-w-2xl mx-auto flex flex-col gap-4 pb-8">

          {/* Title + retour */}
          <div className="flex items-baseline justify-between gap-2">
            <h2 className="text-xl font-bold text-white">Ta lettre</h2>
            <div className="flex items-center gap-2 shrink-0">
              {editMode && (
                <span className="text-xs font-semibold text-violet-400">
                  Mode édition
                </span>
              )}
              {status === 'draft' && (
                <button
                  onClick={() => navigate(`/tiv/lettre-motivation/${id}`)}
                  className="text-xs text-zinc-500 hover:text-zinc-300 transition-colors"
                >
                  ← Retour au chat
                </button>
              )}
            </div>
          </div>

          {/* Letter block */}
          {editMode ? (
            <div className="bg-white/[0.04] border border-violet-500/30 rounded-2xl overflow-hidden">
              <AutoResizeTextarea
                value={text}
                onChange={e => setText(e.target.value)}
                className="w-full bg-transparent px-5 py-5 text-sm leading-relaxed text-zinc-200 resize-none outline-none"
              />
            </div>
          ) : (
            <div
              onClick={hasLetter ? () => setEditMode(true) : undefined}
              className={`bg-white/[0.04] border border-white/[0.06] rounded-2xl px-5 py-5 relative min-h-[200px] ${hasLetter ? 'cursor-text' : ''}`}
            >
              {text
                ? <p className="text-sm leading-relaxed text-zinc-300 whitespace-pre-wrap">{text}</p>
                : <p className="text-sm text-zinc-600 italic">Aucun texte</p>
              }
              {hasLetter && (
                <span className="absolute bottom-2.5 right-4 text-[11px] text-zinc-600 opacity-60">
                  Clique pour modifier
                </span>
              )}
            </div>
          )}

          {/* Toolbar */}
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
            {hasLetter && <>
              {editMode ? (
                <button
                  onClick={handleFinishEdit}
                  disabled={saving}
                  className="px-3 py-1.5 rounded-lg bg-emerald-500/15 border border-emerald-500/30 text-emerald-400 text-xs font-semibold disabled:opacity-50 hover:bg-emerald-500/20 transition-colors"
                >
                  {saving ? 'Enregistrement…' : "✓ Terminer l'édition"}
                </button>
              ) : (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-3 py-1.5 rounded-lg border border-white/[0.08] text-zinc-400 text-xs hover:bg-white/[0.05] transition-colors"
                >
                  ✏️ Modifier
                </button>
              )}
              <button
                onClick={handleCopy}
                disabled={editMode}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-white/[0.08] text-zinc-400 text-xs transition-colors disabled:opacity-30 disabled:cursor-not-allowed hover:bg-white/[0.05] disabled:hover:bg-transparent"
              >
                {copied ? <Check size={11} className="text-emerald-400" /> : <Copy size={11} />}
                {copied ? 'Copié !' : 'Copier'}
              </button>
            </>}
            </div>
            {maxChar !== null && (
              <div className="flex items-center gap-1 text-xs tabular-nums">
                <span className={overLimit ? 'text-red-400 font-semibold' : 'text-zinc-500'}>
                  {charCount}
                </span>
                <span className="text-zinc-600">/</span>
                <MaxCharEditor
                  value={maxChar}
                  onChange={setMaxChar}
                  onBlur={newVal => patch(newVal ?? maxChar)}
                />
              </div>
            )}
          </div>

          {/* Over limit */}
          {overLimit && status === 'draft' && (
            <div className="bg-red-500/[0.08] border border-red-500/25 rounded-xl px-4 py-4">
              <p className="text-sm font-semibold text-red-400 mb-1">
                Ta lettre dépasse de {charCount - maxChar} caractères
              </p>
              <p className="text-xs text-zinc-400 leading-relaxed">
                Parcoursup limite à {maxChar} caractères. Tiv peut t'aider à couper sans perdre l'essentiel.
              </p>
              <button
                onClick={() => {
                  localStorage.setItem(`tiv_pending_casper_${id}`, "C'est trop long, il faut réduire");
                  navigate(`/tiv/lettre-motivation/${id}`);
                }}
                className="mt-3 w-full py-2.5 bg-red-500/15 border border-red-500/30 rounded-lg text-red-400 text-sm font-semibold hover:bg-red-500/20 transition-colors"
              >
                Réduire avec Tiv →
              </button>
            </div>
          )}

          {/* Validate */}
          {hasLetter && !overLimit && status === 'draft' && (
            <button
              onClick={handleValidate}
              disabled={validating || !text}
              className="w-full py-3.5 bg-emerald-500 hover:bg-emerald-400 disabled:opacity-40 disabled:cursor-not-allowed rounded-xl text-sm font-bold text-gray-900 transition-colors mt-2"
            >
              {validating ? 'Validation…' : '✓ Valider ma lettre'}
            </button>
          )}

        </div>
      </div>
    </div>
  );
}
